<script setup>
import { computed, ref, watch } from "vue";
import { User } from "@element-plus/icons-vue";
import { isValidNifNieCif } from "@/utils/nifNieCif";

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  reference: { type: String, required: true },
  applicant: {
    type: Object,
    default: () => ({
      name: "",
      taxId: "",
      locality: "",
      province: "",
      representativeName: "",
      representativeTaxId: "",
      email: "",
      phone: "",
      contactPerson: "",
    }),
  },
  saving: { type: Boolean, default: false },
});

const emit = defineEmits(["update:modelValue", "save"]);
const form = ref({
  name: "",
  taxId: "",
  locality: "",
  province: "",
  representativeName: "",
  representativeTaxId: "",
  email: "",
  phone: "",
  contactPerson: "",
});

const visible = computed({
  get: () => props.modelValue,
  set: (value) => emit("update:modelValue", value),
});

// F6 (fondo_social/v1-20260923/PLAN_FRONTEND.md): la validación de
// DNI/NIE/CIF solo se aplica cuando el valor cambia respecto al guardado —
// un expediente histórico con placeholder IMPORT-XXXXXXXX no debe fallar
// mientras no se toque el campo, igual que hace el backend (B4).
const originalTaxId = ref("");
const originalRepresentativeTaxId = ref("");
const taxIdError = ref("");
const representativeTaxIdError = ref("");

const resetForm = () => {
  form.value = {
    name: props.applicant?.name || "",
    taxId: props.applicant?.taxId || "",
    locality: props.applicant?.locality || "",
    province: props.applicant?.province || "",
    representativeName: props.applicant?.representativeName || "",
    representativeTaxId: props.applicant?.representativeTaxId || "",
    email: props.applicant?.email || "",
    phone: props.applicant?.phone || "",
    contactPerson: props.applicant?.contactPerson || "",
  };
  originalTaxId.value = form.value.taxId;
  originalRepresentativeTaxId.value = form.value.representativeTaxId;
  taxIdError.value = "";
  representativeTaxIdError.value = "";
};

watch(
  () => props.modelValue,
  (isVisible) => {
    if (isVisible) resetForm();
  }
);

const validateTaxIdField = (value, originalValue, errorRef, label) => {
  const trimmed = value.trim();
  if (trimmed === originalValue.trim() || !isValidNifNieCif(trimmed)) {
    errorRef.value = trimmed === originalValue.trim()
      ? ""
      : `${label} no es un DNI, NIE o CIF válido`;
  } else {
    errorRef.value = "";
  }
  return errorRef.value === "";
};

watch(() => form.value.taxId, () => {
  validateTaxIdField(form.value.taxId, originalTaxId.value, taxIdError, "El CIF/NIF");
});
watch(() => form.value.representativeTaxId, () => {
  validateTaxIdField(form.value.representativeTaxId, originalRepresentativeTaxId.value, representativeTaxIdError, "El NIF del representante");
});

const close = () => {
  if (props.saving) return;
  visible.value = false;
};

const save = () => {
  const taxIdOk = validateTaxIdField(form.value.taxId, originalTaxId.value, taxIdError, "El CIF/NIF");
  const representativeTaxIdOk = validateTaxIdField(form.value.representativeTaxId, originalRepresentativeTaxId.value, representativeTaxIdError, "El NIF del representante");
  if (!taxIdOk || !representativeTaxIdOk) return;

  emit("save", {
    name: form.value.name.trim(),
    taxId: form.value.taxId.trim(),
    locality: form.value.locality.trim(),
    province: form.value.province.trim(),
    representativeName: form.value.representativeName.trim(),
    representativeTaxId: form.value.representativeTaxId.trim(),
    contactEmail: form.value.email.trim(),
    contactPhone: form.value.phone.trim(),
    contactPerson: form.value.contactPerson.trim(),
  });
};
</script>

<template>
  <el-dialog v-model="visible" width="620px" :show-close="false" :lock-scroll="true"
    class="social-fund-contact-dialog" @closed="resetForm">
    <section v-if="visible" class="social-fund-contact-dialog__content">
      <img class="social-fund-contact-dialog__close pointer" src="@/assets/images/aspa_blue_small_entrada.png"
        alt="Cerrar" :class="{ 'social-fund-contact-dialog__close--disabled': saving }" @click="close" />

      <header class="social-fund-contact-dialog__header">
        <el-icon>
          <User />
        </el-icon>
        <div>
          <h2>Editar solicitante</h2>
          <p>Solicitud {{ reference }}</p>
        </div>
      </header>

      <div class="row social-fund-contact-dialog__form">
        <div class="col-md-6 col-12">
          <div class="form-item">
            <label for="social-fund-applicant-name">Nombre</label>
            <el-input id="social-fund-applicant-name" v-model="form.name" class="w-100" placeholder="Nombre"
              :disabled="saving" />
          </div>
        </div>
        <div class="col-md-6 col-12">
          <div class="form-item">
            <label for="social-fund-applicant-tax-id">CIF / NIF</label>
            <el-input id="social-fund-applicant-tax-id" v-model="form.taxId" class="w-100" placeholder="CIF / NIF"
              :disabled="saving" />
            <small v-if="taxIdError" class="social-fund-contact-dialog__field-error">{{ taxIdError }}</small>
          </div>
        </div>
        <div class="col-md-6 col-12">
          <div class="form-item">
            <label for="social-fund-applicant-locality">Localidad</label>
            <el-input id="social-fund-applicant-locality" v-model="form.locality" class="w-100"
              placeholder="Localidad" :disabled="saving" />
          </div>
        </div>
        <div class="col-md-6 col-12">
          <div class="form-item">
            <label for="social-fund-applicant-province">Provincia</label>
            <el-input id="social-fund-applicant-province" v-model="form.province" class="w-100"
              placeholder="Provincia" :disabled="saving" />
          </div>
        </div>
        <div class="col-md-6 col-12">
          <div class="form-item">
            <label for="social-fund-contact-person">Persona de contacto</label>
            <el-input id="social-fund-contact-person" v-model="form.contactPerson" class="w-100"
              placeholder="Nombre y apellidos" :disabled="saving" />
          </div>
        </div>
        <div class="col-md-6 col-12">
          <div class="form-item">
            <label for="social-fund-contact-phone">Teléfono de contacto</label>
            <el-input id="social-fund-contact-phone" v-model="form.phone" class="w-100" placeholder="Teléfono"
              :disabled="saving" />
          </div>
        </div>
        <div class="col-12">
          <div class="form-item">
            <label for="social-fund-contact-email">Correo de contacto</label>
            <el-input id="social-fund-contact-email" v-model="form.email" class="w-100" type="email"
              placeholder="correo@ejemplo.com" :disabled="saving" />
          </div>
        </div>
        <div class="col-md-6 col-12">
          <div class="form-item">
            <label for="social-fund-applicant-representative">Representante</label>
            <el-input id="social-fund-applicant-representative" v-model="form.representativeName" class="w-100"
              placeholder="Nombre del representante" :disabled="saving" />
          </div>
        </div>
        <div class="col-md-6 col-12">
          <div class="form-item">
            <label for="social-fund-applicant-representative-tax-id">NIF del representante</label>
            <el-input id="social-fund-applicant-representative-tax-id" v-model="form.representativeTaxId" class="w-100"
              placeholder="NIF del representante" :disabled="saving" />
            <small v-if="representativeTaxIdError" class="social-fund-contact-dialog__field-error">{{ representativeTaxIdError }}</small>
          </div>
        </div>
      </div>

      <footer class="social-fund-contact-dialog__actions">
        <button type="button" class="btn btn_yellow" :disabled="saving" @click="close">CANCELAR</button>
        <button type="button" class="btn btn_red" :disabled="saving" @click="save">
          {{ saving ? "GUARDANDO..." : "GUARDAR CAMBIOS" }}
        </button>
      </footer>
    </section>
  </el-dialog>
</template>

<style lang="scss" scoped>
:global(.el-dialog.social-fund-contact-dialog) {
  background: #f5f3ff;
  border-radius: 20px;
  max-width: calc(100vw - 24px);
  overflow: hidden;
}

.social-fund-contact-dialog :deep(.el-dialog__header) {
  display: none;
}

.social-fund-contact-dialog :deep(.el-dialog__body) {
  padding: 0;
}

.social-fund-contact-dialog__content {
  color: #263052;
  padding: 8px 16px 14px;
  position: relative;
}

.social-fund-contact-dialog__close {
  height: 38px;
  object-fit: contain;
  position: absolute;
  right: 12px;
  top: 8px;
  width: 38px;
}

.social-fund-contact-dialog__close--disabled {
  cursor: not-allowed;
  opacity: .5;
}

.social-fund-contact-dialog__header {
  align-items: center;
  display: flex;
  gap: 12px;
  padding-right: 48px;
}

.social-fund-contact-dialog__header > .el-icon {
  color: $bg-blue;
  font-size: 31px;
}

.social-fund-contact-dialog__header h2 {
  color: $bg-blue;
  font-size: 30px;
  font-weight: 800;
  letter-spacing: -.04em;
  line-height: 1.1;
  margin: 0;
}

.social-fund-contact-dialog__header p {
  color: #737b94;
  font-size: 13px;
  margin: 4px 0 0;
}

.social-fund-contact-dialog__form {
  margin-top: 20px;
  row-gap: 14px;
}

.social-fund-contact-dialog__form .form-item {
  margin-bottom: 0;
}

.social-fund-contact-dialog__form label {
  color: #283150;
  display: block;
  font-size: 13px;
  font-weight: 800;
  margin-bottom: 7px;
}

.social-fund-contact-dialog__field-error {
  color: #f9156f;
  display: block;
  font-size: 12px;
  margin-top: 6px;
}

.social-fund-contact-dialog__actions {
  border-top: 1px solid #e2e5ed;
  display: flex;
  gap: 14px;
  justify-content: flex-end;
  margin: 20px -16px -14px;
  padding: 17px 16px;
}

.social-fund-contact-dialog__actions .btn {
  align-items: center;
  display: inline-flex;
  font-weight: 800;
  justify-content: center;
  margin: 0 !important;
  min-height: 42px;
  min-width: 180px;
}

.social-fund-contact-dialog__actions .btn_yellow {
  color: $bg-blue !important;
}

.social-fund-contact-dialog__actions .btn_red {
  color: #fff !important;
}

@media (max-width: 767px) {
  :global(.el-dialog.social-fund-contact-dialog) {
    max-height: calc(100vh - 24px);
  }

  .social-fund-contact-dialog :deep(.el-dialog__body) {
    max-height: calc(100vh - 24px);
    overflow-y: auto;
  }

  .social-fund-contact-dialog__content {
    padding: 18px 16px 14px;
  }

  .social-fund-contact-dialog__close {
    right: 8px;
    top: 7px;
  }

  .social-fund-contact-dialog__header {
    gap: 10px;
    padding-right: 42px;
  }

  .social-fund-contact-dialog__header > .el-icon {
    font-size: 27px;
  }

  .social-fund-contact-dialog__header h2 {
    font-size: 25px;
  }

  .social-fund-contact-dialog__actions {
    flex-direction: column-reverse;
    gap: 10px;
    margin: 20px -16px -14px;
    padding: 14px 16px;
  }

  .social-fund-contact-dialog__actions .btn {
    width: 100%;
  }
}
</style>
